import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';

export const AuthContext = createContext();
export const ZohoContext = createContext();
export const GdriveContext = createContext();
export const ThemeContext = createContext();
export const PeopleContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => localStorage.getItem('ui_theme') || 'light');

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    localStorage.setItem('ui_theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme((current) => (current === 'dark' ? 'light' : 'dark'));

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

// ─── App auth context (JWT / user session) ────────────────────────────────────
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('session_user')); } catch { return null; }
  });
  const [jwtToken, setJwtToken] = useState(() => {
    const stored = localStorage.getItem('jwt_token');
    if (!stored) return null;
    try {
      const data = JSON.parse(stored);
      return data.token || data;
    } catch {
      return stored;
    }
  });
  const [loading, setLoading] = useState(true);

  // On mount: verify stored token
  useEffect(() => {
    const verify = async () => {
      if (!jwtToken) { setLoading(false); return; }
      try {
        const res = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${jwtToken}` },
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          localStorage.setItem('session_user', JSON.stringify(data));
          setUser(data);
        } else if (res.status === 401) {
          clearSession();
        }
      } catch {
        // Network error or backend cold start — keep the token, don't log out
      } finally {
        setLoading(false);
      }
    };
    verify();
  }, []); // eslint-disable-line

  const clearSession = () => {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('session_user');
    // Legacy keys
    localStorage.removeItem('zoho_token');
    setJwtToken(null);
    setUser(null);
  };

  const login = async (email, password) => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      credentials: 'include'
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login failed');
    const tokenData = {
      token: data.token,
      storedAt: new Date().getTime(),
      expiresAt: new Date().getTime() + (30 * 24 * 60 * 60 * 1000)
    };
    localStorage.setItem('jwt_token', JSON.stringify(tokenData));
    localStorage.setItem('session_user', JSON.stringify(data.user));
    setJwtToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    clearSession();
    window.location.href = '/';
  };

  const updateSessionUser = useCallback((updates) => {
    setUser((current) => {
      const next = { ...(current || {}), ...(updates || {}) };
      localStorage.setItem('session_user', JSON.stringify(next));
      return next;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, jwtToken, loading, login, logout, updateSessionUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export const PeopleProvider = ({ children }) => {
  const { jwtToken, user } = useAuth();
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshPeople = useCallback(async () => {
    if (!jwtToken || user?.role === 'client') { setPeople([]); return []; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/pings/people`, { headers: { Authorization: `Bearer ${jwtToken}` } });
      if (!res.ok) throw new Error('Could not load portal users');
      const next = await res.json();
      setPeople(Array.isArray(next) ? next : []);
      return next;
    } finally {
      setLoading(false);
    }
  }, [jwtToken, user?.role]);

  useEffect(() => {
    if (!jwtToken || user?.role === 'client') { setPeople([]); return undefined; }
    refreshPeople().catch(() => {});
    const timer = setInterval(() => { if (!document.hidden) refreshPeople().catch(() => {}); }, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [jwtToken, user?.id, user?.role, refreshPeople]);

  const byId = useMemo(() => new Map(people.map((person) => [String(person.id), person])), [people]);
  const findPerson = useCallback((id, fallbackName = '') => {
    const exact = id ? byId.get(String(id)) : null;
    if (exact) return exact;
    const normalized = String(fallbackName || '').trim().toLowerCase();
    return people.find((person) => String(person.name || '').trim().toLowerCase() === normalized) || null;
  }, [byId, people]);

  return <PeopleContext.Provider value={{ people, loading, refreshPeople, findPerson }}>{children}</PeopleContext.Provider>;
};

export const usePeople = () => useContext(PeopleContext) || { people: [], loading: false, refreshPeople: async () => [], findPerson: () => null };

// ─── Zoho connection status (server-driven) ──────────────────────────────────
export const ZohoProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const [checked, setChecked] = useState(false);
  // Distinguishes "never connected" from "the stored authorization expired or
  // was revoked". Both need the same button, but they need different words —
  // and a revoked token previously reported as connected, so every Zoho call
  // failed while the UI insisted everything was fine.
  const [needsReauth, setNeedsReauth] = useState(false);
  const { jwtToken, user } = useContext(AuthContext) || {};

  const refresh = useCallback(async () => {
    if (!jwtToken || user?.role !== 'admin') {
      setConnected(false);
      setNeedsReauth(false);
      setChecked(true);
      return false;
    }
    try {
      const res = await fetch(`${API}/mailer/status`, {
        headers: { Authorization: `Bearer ${jwtToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setConnected(!!data.connected);
        setNeedsReauth(!!data.needsReauth);
        setChecked(true);
        return !!data.connected;
      }
    } catch { /* network */ }
    setChecked(true);
    return false;
  }, [jwtToken, user]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <ZohoContext.Provider value={{ connected, checked, needsReauth, refresh }}>
      {children}
    </ZohoContext.Provider>
  );
};

export const useZoho = () => useContext(ZohoContext);

// ─── Google Drive connection status (server-driven) ──────────────────────────
export const GdriveProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const [checked, setChecked] = useState(false);
  const { jwtToken, user } = useContext(AuthContext) || {};

  const refresh = useCallback(async () => {
    if (!jwtToken || user?.role !== 'admin') {
      setConnected(false);
      setChecked(true);
      return false;
    }
    try {
      const res = await fetch(`${API}/gdrive/status`, {
        headers: { Authorization: `Bearer ${jwtToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setConnected(!!data.connected);
        setChecked(true);
        return !!data.connected;
      }
    } catch { /* network */ }
    setChecked(true);
    return false;
  }, [jwtToken, user]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <GdriveContext.Provider value={{ connected, checked, refresh }}>
      {children}
    </GdriveContext.Provider>
  );
};

export const useGdrive = () => useContext(GdriveContext);
